import { Alert, Box, FormHelperText, Stack, SxProps, Theme } from "@mui/material";
import { isEmpty } from "lodash-es";
import { useMemo, useState } from "react";

import { BaseFormikValues, ValidationErrors } from "@/common/ts/error";
import { ValidationHelper, ValidationInfo } from "@/common/validation";

import AppIcon from "../Icons/AppIcon";
import AppLink from "../Link/AppLink";
import ValidationInfoModal from "./ValidationInfoModal";

interface Props {
  errors: ValidationErrors<BaseFormikValues> | null | undefined;
  generalError?: string;
  validationInfo?: ValidationInfo;
  withDetails?: boolean;
  sx?: SxProps<Theme>;
}

/** Displays general validation error.
 *  E.g. form-level error, not related to specific field.
 */
export default function GeneralValidationError({
  errors,
  generalError,
  validationInfo,
  withDetails = true,
  sx,
}: Props) {
  const [isValidationInfoModalOpen, setIsValidationInfoModalOpen] = useState(false);

  const generalErrorComputed = useMemo(
    () => generalError || ValidationHelper.getGeneralErrorsAsString(errors),
    [errors, generalError],
  );
  const validationInfoComputed = useMemo(
    () =>
      validationInfo
        ? validationInfo
        : errors instanceof ValidationInfo
          ? errors
          : ValidationHelper.getValidationInfoFromValidationErrors(errors),
    [errors, validationInfo],
  );
  const isShowDetails = withDetails && !!validationInfoComputed;

  if (!errors || isEmpty(generalErrorComputed)) {
    return null;
  }

  return (
    <Box sx={sx}>
      <FormHelperText component='div' error>
        <Alert severity='error' sx={{ "& .MuiAlert-message": { flex: 1 } }}>
          <Stack
            sx={{ width: "100%" }}
            spacing={0.5}
            direction={"row"}
            justifyContent={"space-between"}
          >
            <Box>{generalErrorComputed}</Box>

            {isShowDetails && (
              <AppLink
                to='#'
                isInheritStyles
                onClick={(e) => {
                  e.preventDefault();
                  setIsValidationInfoModalOpen(true);
                }}
                sx={{ color: (t) => t.palette.error.main, textUnderlineOffset: "3px" }}
              >
                {`Report the issue`.toUpperCase()}{" "}
                <AppIcon of='report' fontSize='medium' sx={{ ml: 1, verticalAlign: "middle" }} />
              </AppLink>
            )}
          </Stack>
        </Alert>
      </FormHelperText>

      {isShowDetails && validationInfoComputed && (
        <ValidationInfoModal
          open={isValidationInfoModalOpen}
          onClose={() => {
            setIsValidationInfoModalOpen(false);
          }}
          problemDetails={validationInfoComputed.problemDetails}
          validationInfo={validationInfoComputed}
        />
      )}
    </Box>
  );
}
